import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Drawer,
  Skeleton,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DashboardHeader from "../Dashboard/DashboardHeader";
import { date, entity_id, location, generateOrderId, Platform } from "../Utils/Const";
import { Alert } from "@mui/material";
import { baseapi } from "../Environmentalurl";
import api from "../Utils/Api";
import getDeviceId from "../Utils/useIPAddress";

// ─── File upload constraints ─────────────────────────────────────────────────
const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg", "application/pdf"];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

// ─── Date Formatter ──────────────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const h = String(hours % 12 || 12).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${h}:${minutes} ${ampm}`;
}

// ─── Normalize a single ticket row from any response shape ──────────────────
function normalizeTicket(row) {
  return {
    mobileNumber: row.mobileNumber || "—",
    loggedDate: row.createdDate || null,
    ticketId: row.ticketId || "—",
    refNumber: row.refNumber || "—",
    issueService: row.issueService || "—",
    status: row.ticketStatus || "—",
  };
}

// ─── Derive Open/Closed from any status string ──────────────────────────────
function resolveStatusLabel(status) {
  if (!status || status === "—") return "—";
  const s = status.toLowerCase();
  if (s.includes("close") || s.includes("resolved")) return "Closed";
  return "Open";
}

// ─── Status color helper ─────────────────────────────────────────────────────
function getStatusColor(status) {
  if (!status) return { bg: "#f0f0f0", color: "#888", dot: "#bbb" };
  const s = status.toLowerCase();
  if (s.includes("close") || s.includes("resolved"))
    return { bg: "#e8f5e9", color: "#16a34a", dot: "#22c55e" };
  if (s.includes("progress") || s.includes("assigned"))
    return { bg: "#fff8e1", color: "#f57f17", dot: "#ffc107" };
  if (s.includes("generate") || s.includes("open"))
    return { bg: "#e3f2fd", color: "#1565c0", dot: "#2196f3" };
  return { bg: "#f3e5f5", color: "#6a1b9a", dot: "#9c27b0" };
}

// ─── Timeline step derived status ───────────────────────────────────────────
function getTimelineStepStyle(index, total) {
  if (index < total - 1) return "completed";
  return "current";
}

// ─── File helpers ─────────────────────────────────────────────────────────────
function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(nameOrUrl) {
  if (!nameOrUrl) return "";
  const clean = nameOrUrl.split("?")[0];
  const parts = clean.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
}

export default function Grievance() {
  const [complaints, setComplaints] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filterType, setFilterType] = useState("All");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showDialogAlert, setShowDialogAlert] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Track Ticket State ───────────────────────────────────────────────────
  const [trackDrawerOpen, setTrackDrawerOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState("");

  const [formData, setFormData] = useState({
    issueService: "Feedback", // hardcoded (dropdown removed)
    description: "",
  });
  const [errors, setErrors] = useState({});

  // ── Attachment state ─────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState("");

  const ip = getDeviceId();
  const mobilenumber = sessionStorage.getItem("mobileNumber");
  const sessionid = sessionStorage.getItem("SessionID");
  const username = sessionStorage.getItem("NameOnCard");

  // ─── Normalized + filtered list ──────────────────────────────────────────
  const normalizedComplaints = complaints.map((row) => {
    const n = normalizeTicket(row);
    return { ...n, statusLabel: resolveStatusLabel(n.status) };
  });

  const filteredComplaints = normalizedComplaints
    .filter((c) => filterType === "All" || c.statusLabel === filterType)
    .filter((c) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const formattedDate = c.loggedDate
        ? new Date(c.loggedDate).toLocaleDateString("en-GB")
        : "";
      return (
        (c.ticketId || "").toLowerCase().includes(q) ||
        (c.refNumber || "").toLowerCase().includes(q) ||
        formattedDate.toLowerCase().includes(q)
      );
    });

  // ─── Fetch All Tickets ────────────────────────────────────────────────────
  const fetchGrievances = async () => {
    setLoading(true);
    try {
      const payload = {
        MobileNumber: mobilenumber,
        SessionID: sessionid,
        EntityID: entity_id,
        Platform: Platform,
        GeoLocation: location,
        MachineID: ip,
      };

      const response = await api.post(`${baseapi}/All-ticket`, payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const data = response.data;
      let combined = [];

      if (data.StatusCode === "1000") {
        combined = Array.isArray(data.Data?.content) ? data.Data.content : [];
      } else if (data.Response_Code === "1000") {
        const openData = data.Info?.Table || [];
        const closedData = data.Info?.Table1 || [];
        combined = [...openData, ...closedData];
      } else if (Array.isArray(data.data)) {
        combined = data.data;
      } else if (Array.isArray(data.tickets)) {
        combined = data.tickets;
      } else if (Array.isArray(data)) {
        combined = data;
      }

      setComplaints(combined);
    } catch (error) {
      console.error("Fetch grievances error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ip) {
      fetchGrievances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ip]);

  // ─── Cleanup object URL when file changes / dialog closes ────────────────
  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  // ─── Track Ticket API Call ────────────────────────────────────────────────
  const handleRowClick = async (ticketId) => {
    setTrackDrawerOpen(true);
    setTrackingData(null);
    setTrackingError("");
    setTrackingLoading(true);

    try {
      const payload = {
        MobileNumber: mobilenumber,
        TicketId: ticketId,
        SessionID: sessionid,
        EntityID: entity_id,
        Platform: Platform,
        GeoLocation: location,
        MachineID: ip,
      };

      const response = await api.post(
        `https://taapp.transactionanalysts.com:8089/apiwallet/api/services/track-ticket`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      const data = response.data;
      if (data.StatusCode === "1000") {
        setTrackingData(data.Data);
      } else {
        setTrackingError(data.Message || "Unable to fetch ticket details.");
      }
    } catch (err) {
      console.error("Track ticket error:", err);
      setTrackingError("Failed to load ticket details. Please try again.");
    } finally {
      setTrackingLoading(false);
    }
  };

  // ─── File select / validate / remove ──────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file: "Only PNG, JPEG, or PDF files are allowed",
      }));
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrors((prev) => ({
        ...prev,
        file: "File size must not exceed 5MB",
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, file: "" }));

    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);

    setSelectedFile(file);
    setFilePreviewUrl(
      file.type === "application/pdf" ? "" : URL.createObjectURL(file),
    );
  };

  const handleRemoveFile = () => {
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setSelectedFile(null);
    setFilePreviewUrl("");
    setErrors((prev) => ({ ...prev, file: "" }));
  };

  // ─── Raise Ticket ─────────────────────────────────────────────────────────
  const raiseTicket = async () => {
    console.log("raiseTicket called →", { desc: formData.description });

    if (!mobilenumber || !sessionid) {
      setSnackbarMessage("Session expired. Please login again.");
      setSnackbarSeverity("error");
      setShowDialogAlert(true);
      return;
    }
    setIsSubmitting(true);

    try {
      // ── STEP 1: /raise ──
      const freshOrderId = generateOrderId({ mobileNumber: mobilenumber });

      const raisePayload = {
        MobileNumber: mobilenumber,
        Name: username,
        IssueService: "Feedback", // hardcoded
        IssueDescription: formData.description,
        OrderId: freshOrderId,
        SessionID: sessionid,
        EntityID: entity_id,
        MachineID: ip,
        Platform: Platform,
        osVersion: "Windows 11",
        GeoLocation: location,
      };

      const raiseResponse = await api.post(`${baseapi}/raise`, raisePayload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const {
        Encrypted_data,
        Platform: encPlatform,
        Encrypted_skey,
      } = raiseResponse.data || {};

      // ── STEP 2: /raise/upload — always runs, file is optional ──
      const uploadForm = new FormData();
      uploadForm.append("encryptedData", Encrypted_data);
      uploadForm.append("platform", encPlatform);
      uploadForm.append("encryptedSkey", Encrypted_skey);

      if (selectedFile) {
        uploadForm.append("file", selectedFile);
      }

      const uploadResponse = await api.post(
        `${baseapi}/raise/upload`,
        uploadForm,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      const data = uploadResponse.data;
      console.log("Raise ticket response:", data);

      const isSuccess = String(data.StatusCode) === "1000";
      const isDuplicate =
        String(data.StatusCode) === "409" ||
        (data.Message || "").toLowerCase().includes("duplicate") ||
        (data.Message || "").toLowerCase().includes("already exists");

      setSnackbarMessage(
        isSuccess
          ? `${data.Message} (ID: ${data.TicketId})`
          : isDuplicate
            ? "Please enter a proper issue to resolve"
            : "Unable to process request. Please try again",
      );
      setSnackbarSeverity(isSuccess ? "success" : "error");
      setShowDialogAlert(true);

      if (isSuccess) {
        const ticketData = data.Data || {};

        const newTicket = {
          ticketId: ticketData.ticketId || data.TicketId,
          mobileNumber: ticketData.mobileNumber || mobilenumber,
          refNumber: ticketData.refNumber,
          issueService: "Feedback",
          issueDescription: ticketData.issueDescription || formData.description,
          createdDate: ticketData.createdDate || date,
          ticketStatus: ticketData.ticketStatus || "Ticket Generate",
          ticketPriority: ticketData.ticketPriority,
          filePath: ticketData.filePath || "",
        };

        setComplaints((prev) => [newTicket, ...prev]);

        setTimeout(() => {
          setShowDialogAlert(false);
          setOpenDialog(false);
          setFormData({ issueService: "Feedback", description: "" });
          handleRemoveFile();
          setIsSubmitting(false);
          fetchGrievances();
        }, 1500);
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error(error);

      let message = "Unable to process request. Please try again";

      if (
        error.response?.data?.message?.includes("Maximum upload size exceeded") ||
        error.response?.data?.trace?.includes("MaxUploadSizeExceededException") ||
        error.response?.data?.trace?.includes("FileSizeLimitExceededException")
      ) {
        message = "File size exceeded. Please upload a file smaller than 1 MB.";
      }

      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setShowDialogAlert(true);
      setIsSubmitting(false);
    }
  };

  // ─── Validate & Submit ────────────────────────────────────────────────────
  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.description) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 30) {
      newErrors.description = "Minimum 30 characters required";
    }
    if (errors.file) newErrors.file = errors.file;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    raiseTicket();
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFormData({ issueService: "Feedback", description: "" });
    setErrors({});
    setShowDialogAlert(false);
    setIsSubmitting(false);
    handleRemoveFile();
  };

  // ─── Ticket Status Badge ──────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const colors = getStatusColor(status);
    return (
      <Box
        display="inline-flex"
        alignItems="center"
        gap={0.6}
        sx={{
          background: colors.bg,
          color: colors.color,
          borderRadius: "20px",
          px: 1.4,
          py: 0.3,
          fontSize: "11.5px",
          fontWeight: 600,
          letterSpacing: "0.3px",
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: colors.dot,
            flexShrink: 0,
          }}
        />
        {status}
      </Box>
    );
  };

  // ─── Info Card sub-component ──────────────────────────────────────────────
  const InfoCard = ({ icon, label, value, highlight }) => (
    <Box
      sx={{
        background: "#fff",
        border: highlight ? "1.5px solid #ef9a9a" : "1px solid #e8eaf0",
        borderRadius: "10px",
        px: 1.5,
        py: 1.2,
      }}
    >
      <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
        <Box sx={{ color: highlight ? "#e53935" : "#1bb45b" }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: 10.5,
            color: highlight ? "#e53935" : "#aaa",
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: highlight ? "#c62828" : "#333",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // ─── Tracking Drawer ──────────────────────────────────────────────────────
  const TrackingDrawer = () => {
    const priorityColors = {
      HIGH: { bg: "#fdecea", color: "#c62828" },
      MEDIUM: { bg: "#fff8e1", color: "#e65100" },
      LOW: { bg: "#e8f5e9", color: "#16a34a" },
    };

    const priorityColor = trackingData
      ? priorityColors[trackingData.ticketPriority] || {
          bg: "#f3f4f6",
          color: "#555",
        }
      : null;

    return (
      <Drawer
        anchor="right"
        open={trackDrawerOpen}
        onClose={() => setTrackDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480 },
            background: "#f8f9fc",
            boxShadow: "-4px 0 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1bb45b 0%, #0d8f47 100%)",
            px: 3,
            pt: 3,
            pb: 3.5,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -20,
              right: 40,
              width: 70,
              height: 70,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
            }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: 12,
                  letterSpacing: 1,
                  mb: 0.3,
                }}
              >
                TICKET TRACKER
              </Typography>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>
                {trackingLoading ? "Loading..." : trackingData?.ticketId || "—"}
              </Typography>
              {trackingData && (
                <Typography
                  sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12.5, mt: 0.5 }}
                >
                  {trackingData.issueService}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setTrackDrawerOpen(false)}
              sx={{
                color: "#fff",
                background: "rgba(255,255,255,0.15)",
                "&:hover": { background: "rgba(255,255,255,0.25)" },
                width: 34,
                height: 34,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {trackingData && (
            <Box display="flex" gap={1} mt={2}>
              <Box
                sx={{
                  background: priorityColor.bg,
                  color: priorityColor.color,
                  borderRadius: "12px",
                  px: 1.5,
                  py: 0.3,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {trackingData.ticketPriority} PRIORITY
              </Box>
              <Box
                sx={{
                  background: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  borderRadius: "12px",
                  px: 1.5,
                  py: 0.3,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {trackingData.ticketStatus}
              </Box>
              {trackingData.tatBreached && (
                <Box
                  sx={{
                    background: "#fdecea",
                    color: "#c62828",
                    borderRadius: "12px",
                    px: 1.5,
                    py: 0.3,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  TAT BREACHED
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Drawer Content */}
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {trackingLoading && (
            <Box sx={{ px: 3, py: 3 }}>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ mb: 3 }}>
                  <Skeleton variant="text" width="40%" height={18} sx={{ mb: 1 }} />
                  <Skeleton variant="rounded" height={60} sx={{ borderRadius: 2 }} />
                </Box>
              ))}
              <Skeleton variant="text" width="40%" height={18} sx={{ mb: 2 }} />
              {[1, 2].map((i) => (
                <Box key={i} display="flex" gap={2} mb={2}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Box flex={1}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {!trackingLoading && trackingError && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={8}
              px={3}
              gap={1.5}
            >
              <InfoOutlinedIcon sx={{ fontSize: 44, color: "#e57373" }} />
              <Typography fontWeight={600} color="#555" textAlign="center">
                {trackingError}
              </Typography>
              <Button
                onClick={() => trackingData && handleRowClick(trackingData.ticketId)}
                variant="outlined"
                size="small"
                sx={{ mt: 1, borderRadius: 2, textTransform: "none" }}
              >
                Retry
              </Button>
            </Box>
          )}

          {!trackingLoading && trackingData && (
            <Box sx={{ px: 3, py: 3 }}>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mb={2.5}>
                <InfoCard
                  icon={<AssignmentOutlinedIcon sx={{ fontSize: 16 }} />}
                  label="Ref Number"
                  value={trackingData.refNumber || "—"}
                />
                <InfoCard
                  icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                  label="Assigned To"
                  value={trackingData.assignedOfficer || "Unassigned"}
                />
                <InfoCard
                  icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  label="Opened On"
                  value={formatDate(trackingData.createdDate)}
                />
                <InfoCard
                  icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  label="Due By"
                  value={formatDate(trackingData.dueTime)}
                  highlight={trackingData.tatBreached}
                />
              </Box>

              {trackingData.issueDescription && (
                <Box
                  sx={{
                    background: "#fff",
                    border: "1px solid #e8eaf0",
                    borderRadius: "12px",
                    p: 2,
                    mb: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#888",
                      fontWeight: 600,
                      letterSpacing: 0.8,
                      mb: 0.8,
                      textTransform: "uppercase",
                    }}
                  >
                    Issue Description
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: "#333", lineHeight: 1.7 }}>
                    {trackingData.issueDescription}
                  </Typography>
                </Box>
              )}

              {trackingData.filePath && (
                <Box
                  sx={{
                    background: "#fff",
                    border: "1px solid #e8eaf0",
                    borderRadius: "12px",
                    p: 2,
                    mb: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#888",
                      fontWeight: 600,
                      letterSpacing: 0.8,
                      mb: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    Attachment
                  </Typography>
                  <Box
                    component="a"
                    href={trackingData.filePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      textDecoration: "none",
                      p: 1.2,
                      borderRadius: "10px",
                      border: "1px solid #e0f2e9",
                      background: "#f6fbf8",
                      "&:hover": { background: "#eef9f2" },
                    }}
                  >
                    {/\.(png|jpe?g)$/i.test(trackingData.filePath) ? (
                      <Box
                        component="img"
                        src={trackingData.filePath}
                        alt="attachment"
                        sx={{
                          width: 44,
                          height: 44,
                          objectFit: "cover",
                          borderRadius: "8px",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 30, color: "#1bb45b", flexShrink: 0 }}
                      />
                    )}
                    <Box sx={{ overflow: "hidden" }}>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "#1bb45b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        View attachment
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#888" }}>
                        {getFileExtension(trackingData.filePath)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Typography
                sx={{
                  fontSize: 11,
                  color: "#888",
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  mb: 2,
                }}
              >
                Activity Timeline
              </Typography>

              {Array.isArray(trackingData.timeline) &&
              trackingData.timeline.length > 0 ? (
                <Box sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      left: 17,
                      top: 18,
                      bottom: 18,
                      width: 2,
                      background: "linear-gradient(to bottom, #1bb45b33, #e0e0e0)",
                      zIndex: 0,
                    }}
                  />

                  {trackingData.timeline.map((step, idx) => {
                    const stepState = getTimelineStepStyle(
                      idx,
                      trackingData.timeline.length,
                    );
                    const isCurrent = stepState === "current";
                    const isCompleted = stepState === "completed";

                    return (
                      <Box
                        key={idx}
                        display="flex"
                        gap={2}
                        sx={{ position: "relative", zIndex: 1, mb: 2 }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isCurrent
                              ? "linear-gradient(135deg, #1bb45b, #0d8f47)"
                              : isCompleted
                                ? "#e8f5e9"
                                : "#f0f0f0",
                            border: isCurrent
                              ? "none"
                              : isCompleted
                                ? "2px solid #22c55e"
                                : "2px solid #ddd",
                            boxShadow: isCurrent
                              ? "0 4px 12px rgba(27,180,91,0.35)"
                              : "none",
                          }}
                        >
                          {isCurrent ? (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: "#fff",
                                animation: "pulse 1.5s infinite",
                                "@keyframes pulse": {
                                  "0%": { opacity: 1, transform: "scale(1)" },
                                  "50%": { opacity: 0.6, transform: "scale(0.8)" },
                                  "100%": { opacity: 1, transform: "scale(1)" },
                                },
                              }}
                            />
                          ) : (
                            <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                          )}
                        </Box>

                        <Box
                          sx={{
                            background: "#fff",
                            border: isCurrent ? "1.5px solid #1bb45b" : "1px solid #eee",
                            borderRadius: "12px",
                            p: 2,
                            flex: 1,
                            boxShadow: isCurrent
                              ? "0 4px 16px rgba(27,180,91,0.1)"
                              : "0 1px 4px rgba(0,0,0,0.04)",
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={0.5}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: isCurrent ? "#1bb45b" : "#333",
                              }}
                            >
                              {step.status}
                            </Typography>
                            {isCurrent && (
                              <Box
                                sx={{
                                  background: "#e8f5e9",
                                  color: "#16a34a",
                                  borderRadius: "8px",
                                  px: 1,
                                  py: 0.2,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                }}
                              >
                                CURRENT
                              </Box>
                            )}
                          </Box>

                          {step.comments && (
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                color: "#666",
                                lineHeight: 1.6,
                                mb: 1,
                              }}
                            >
                              {step.comments}
                            </Typography>
                          )}

                          <Box display="flex" gap={2} flexWrap="wrap">
                            {step.updatedAt && (
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "#aaa",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                }}
                              >
                                <AccessTimeIcon sx={{ fontSize: 12 }} />
                                {formatDate(step.updatedAt)}
                              </Typography>
                            )}
                            {step.updatedBy && (
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "#aaa",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                }}
                              >
                                <PersonOutlineIcon sx={{ fontSize: 12 }} />
                                {step.updatedBy}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                  <RadioButtonUncheckedIcon sx={{ fontSize: 36, color: "#ddd" }} />
                  <Typography color="text.secondary" fontSize={13}>
                    No timeline activity yet
                  </Typography>
                </Box>
              )}

              {(trackingData.channelSource || trackingData.deviceType) && (
                <Box
                  sx={{
                    mt: 2,
                    background: "#f0f4ff",
                    borderRadius: "10px",
                    px: 2,
                    py: 1.2,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  {trackingData.channelSource && (
                    <Typography sx={{ fontSize: 11.5, color: "#5c6bc0" }}>
                      <strong>Source:</strong> {trackingData.channelSource}
                    </Typography>
                  )}
                  {trackingData.deviceType && (
                    <Typography sx={{ fontSize: 11.5, color: "#5c6bc0" }}>
                      <strong>Device:</strong> {trackingData.deviceType}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <DashboardHeader />
      <Box sx={{ p: 5, px: 10 }}>
        <Paper elevation={0} sx={{ borderRadius: "30px", p: 4, background: "#f4f5f7" }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold" mb={3}>
              COMPLAINTS
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                background: "#1bb45b",
                color: "#fff",
                textTransform: "none",
                px: 3,
                borderRadius: "10px",
                "&:hover": { background: "#17a352" },
              }}
            >
              Create Ticket
            </Button>
          </Box>

          <Divider sx={{ mb: 3, mt: 2 }} />

          {/* Search + Filter */}
          <Box display="flex" justifyContent="space-between" mb={3}>
            <TextField
              size="small"
              placeholder="Search by mobile, description, ticket no."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: "340px", background: "#fff", borderRadius: "8px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Box display="flex" gap={2}>
              <Button
                startIcon={<FilterListIcon />}
                variant="outlined"
                onClick={(e) => setFilterAnchor(e.currentTarget)}
              >
                {filterType === "All" ? "Filter" : filterType}
              </Button>
              <Menu
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={() => setFilterAnchor(null)}
              >
                {["All", "Open", "Closed"].map((opt) => (
                  <MenuItem
                    key={opt}
                    selected={filterType === opt}
                    onClick={() => {
                      setFilterType(opt);
                      setFilterAnchor(null);
                    }}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>

          {/* Table Header */}
          <Box
            display="grid"
            gridTemplateColumns="1.4fr 1.4fr 1.6fr 1.6fr 1.8fr 1.4fr"
            fontWeight={600}
            fontSize="14px"
            color="black"
            px={3}
            py={1}
            mb={1.5}
            width="100%"
            columnGap={3}
          >
            <Typography fontWeight="bold">Mobile Number</Typography>
            <Typography fontWeight="bold">Ticket ID</Typography>
            <Typography fontWeight="bold">Ref Number</Typography>
            <Typography fontWeight="bold">Service</Typography>
            <Typography fontWeight="bold">Created Date</Typography>
            <Typography fontWeight="bold">Status</Typography>
          </Box>

          {/* Loading / Empty / Rows */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="text.secondary" fontSize={14}>
                Loading tickets...
              </Typography>
            </Box>
          ) : filteredComplaints.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={6}
              gap={1}
            >
              <ConfirmationNumberOutlinedIcon sx={{ fontSize: 48, color: "#bbb" }} />
              <Typography color="text.secondary" fontSize={14}>
                No complaints found
              </Typography>
            </Box>
          ) : (
            filteredComplaints.map((row, index) => (
              <Tooltip
                key={index}
                title="Click to track ticket status"
                placement="top"
                arrow
              >
                <Box
                  onClick={() => handleRowClick(row.ticketId)}
                  display="grid"
                  gridTemplateColumns="1.4fr 1.4fr 1.6fr 1.6fr 1.8fr 1.4fr"
                  alignItems="center"
                  width="100%"
                  columnGap={3}
                  sx={{
                    background: "#fff",
                    px: 3,
                    py: 1.5,
                    mb: 1.2,
                    borderRadius: "10px",
                    fontSize: "13px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    transition: "all 0.18s ease",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 4px 14px rgba(27,180,91,0.13)",
                      background: "#f7fef9",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Typography sx={{ wordBreak: "break-word" }}>
                    {row.mobileNumber}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={0.6}>
                    <Typography
                      sx={{
                        wordBreak: "break-word",
                        color: "#1bb45b",
                        fontWeight: 600,
                        fontSize: 12.5,
                      }}
                    >
                      {row.ticketId}
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: 13, color: "#1bb45b", opacity: 0.7 }} />
                  </Box>

                  <Typography sx={{ wordBreak: "break-word" }}>
                    {row.refNumber}
                  </Typography>

                  <Typography sx={{ wordBreak: "break-word" }}>
                    {row.issueService}
                  </Typography>

                  <Typography sx={{ wordBreak: "break-word" }}>
                    {formatDate(row.loggedDate)}
                  </Typography>

                  <Box>
                    <StatusBadge status={row.status} />
                  </Box>
                </Box>
              </Tooltip>
            ))
          )}

          {/* Pagination */}
          <Box display="flex" justifyContent="flex-end" mt={3} gap={1} alignItems="center">
            <Typography fontSize={12} color="text.secondary">
              {filteredComplaints.length} result
              {filteredComplaints.length !== 1 ? "s" : ""}
            </Typography>
            <IconButton size="small">
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton size="small">
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Box>

      {/* Ticket Tracking Drawer */}
      <TrackingDrawer />

      {/* Create Ticket Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        BackdropProps={{
          sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(0,0,0,0.4)" },
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            padding: 2,
            background: "rgba(255,255,255,0.97)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "20px", pb: 0.5 }}>
          Raise a Ticket
          <Typography variant="body2" color="text.secondary" fontWeight={400} mt={0.5}>
            Fill in the details and we'll get back to you shortly.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {showDialogAlert && (
            <Alert
              severity={snackbarSeverity}
              variant="filled"
              sx={{ mb: 2, borderRadius: "8px", fontWeight: 500 }}
            >
              {snackbarMessage}
            </Alert>
          )}

          {/* Read-only fields */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={1}>
            <TextField
              fullWidth
              label="Full Name"
              variant="outlined"
              value={username || ""}
              disabled
              size="small"
              sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#444" } }}
            />
            <TextField
              fullWidth
              label="Mobile Number"
              variant="outlined"
              value={mobilenumber || ""}
              disabled
              size="small"
              sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#444" } }}
            />
          </Box>

          {/* Description */}
          <TextField
            fullWidth
            multiline
            autoComplete="off"
            rows={4}
            label="Describe your issue"
            margin="normal"
            variant="outlined"
            value={formData.description}
            onChange={(e) => {
              const value = e.target.value;
              setFormData({ ...formData, description: value });
              if (value.trim().length >= 30)
                setErrors((prev) => ({ ...prev, description: "" }));
            }}
            error={!!errors.description}
            helperText={
              errors.description ||
              `${formData.description.length}/300 characters minimum`
            }
          />

          {/* Attachment */}
          <Box mt={2}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#444", mb: 0.8 }}>
              Attachment{" "}
              <Typography
                component="span"
                sx={{ fontSize: 12, color: "#999", fontWeight: 400 }}
              >
                (optional — PNG, JPEG or PDF, max 5MB)
              </Typography>
            </Typography>

            {!selectedFile ? (
              <Button
                component="label"
                
                variant="outlined"
                startIcon={<AttachFileIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  borderColor: errors.file ? "#d32f2f" : "#cfd8dc",
                  color: errors.file ? "#d32f2f" : "#444",
                }}
              >
                Choose file
                <input
                  type="file"
                  hidden
                  accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                  onChange={handleFileSelect}
                />
              </Button>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.2,
                  borderRadius: "10px",
                  border: "1px solid #e0f2e9",
                  background: "#f6fbf8",
                }}
              >
                {filePreviewUrl ? (
                  <Box
                    component="img"
                    src={filePreviewUrl}
                    alt="preview"
                    sx={{
                      width: 44,
                      height: 44,
                      objectFit: "cover",
                      borderRadius: "8px",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: 32, color: "#1bb45b", flexShrink: 0 }}
                  />
                )}
                <Box sx={{ overflow: "hidden", flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#333",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#888" }}>
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={handleRemoveFile}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {errors.file && (
              <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                {errors.file}
              </FormHelperText>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleDialogClose}
            sx={{ textTransform: "none", color: "#666", borderRadius: "10px", px: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              color: "#ffffff",
              borderRadius: "10px",
              px: 4,
              background: "#1bb45b",
              boxShadow: "0 8px 20px rgba(140, 209, 161, 0.4)",
              "&:hover": { background: "#17a352" },
              "&.Mui-disabled": { background: "#ccc", color: "#2f0b0b" },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit Ticket"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}